import React, { useEffect } from "react";
import { useGlobalNotifier, useMessage } from "@vrf/fr-vrf-core-react";
import {
  FrVrfButton,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfRadioButtonField,
} from "@vrf/fr-vrf-web-components-react";
import { createModal } from "../../modal";
import { ApplicationMessageScreen } from "../../../messages/screen-message";
import { useGetGeneralCodes } from "../../../services/data-list";
import {
  UpdateProjectRequest,
  useUpdateProject,
} from "../../../services/project";
import {
  useInputCostEstimationDialogState,
  Option,
  InputCostEstimationDialogInitialState,
} from ".";
import { usePageNavigation } from "../../../pages/use-page-navigation";

const MANUAL_LINK = {
  tooltip: "4. Confirm Estimation",
  value:
    "https://confluence.fastretailing.com/pages/viewpage.action?pageId=847035106",
};

export const InputCostEstimationDialogContent: React.FC = () => {
  const { showToast, showConfirmationDialog, errorMessages, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const [getGeneralCodesResponse, getGeneralCodesRequest] =
    useGetGeneralCodes();
  const [updateProjectResponse, updateProjectRequest] = useUpdateProject();
  const [pageState, setPageState] = useInputCostEstimationDialogState();
  const { moveToModalPage, closeModalPage } = usePageNavigation();

  const closeModal = (isSaved: boolean) => {
    if (!isSaved) {
      resetUserError();
      setPageState(InputCostEstimationDialogInitialState);
      return;
    }
    closeModalPage();

    setPageState((prevState) => ({
      ...prevState,
      isShow: false,
      status: {
        ...prevState.status,
        isSaved: true,
      },
    }));
  };

  const onInitialDisplayed = (): void => {
    moveToModalPage();
    resetUserError();
    const cdTypes = ["in_def_common_flag_type"];
    getGeneralCodesRequest({ cdTypes: cdTypes });
  };
  useEffect(onInitialDisplayed, []);

  const ongetGetGeneralCodes = () => {
    let pjtBdgtAcqstnReqType = [] as Option[];
    let statsOfAgreementOnBudgetaryType = [] as Option[];
    const generalCode = getGeneralCodesResponse?.gnrlCdTypeList ?? [];

    generalCode.forEach((e) => {
      switch (e.cdType) {
        case "in_def_common_flag_type":
          pjtBdgtAcqstnReqType = e.cdList
            .map((e) => {
              return {
                label: e.label,
                value: e.value,
              };
            })
            .reverse();
          statsOfAgreementOnBudgetaryType = e.cdList
            .map((e) => {
              return {
                label: e.label,
                value: e.value,
              };
            })
            .reverse();
          break;
      }
    });

    setPageState((prevState) => ({
      ...prevState,
      codes: {
        pjtBdgtAcqstnReqType: pjtBdgtAcqstnReqType,
        statsOfAgreementOnBudgetaryType: statsOfAgreementOnBudgetaryType,
      },
    }));
  };
  useEffect(ongetGetGeneralCodes, [getGeneralCodesResponse]);

  const onChangeValueHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    const isValid = event.detail.isValid;

    setPageState((prevState) => ({
      ...prevState,
      edited: {
        ...prevState.edited,
        [name]: value,
      },
      status: {
        ...prevState.status,
        invalidList: isValid
          ? prevState.status.invalidList.filter((e) => e !== name)
          : prevState.status.invalidList.concat(name),
      },
    }));
  };

  const onClickCancel = () => {
    if (JSON.stringify(pageState.saved) === JSON.stringify(pageState.edited)) {
      closeModal(false);
    } else {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        type: "confirm",
        onOkButtonClicked: () => {
          closeModal(false);
        },
      });
    }
  };

  const onClickSave = () => {
    const updateProject = () => {
      resetUserError();
      const request: UpdateProjectRequest = {
        pjtId: pageState.pjtId,
        costEstimation: { ...pageState.edited },
        updateDateTime: pageState.updateDateTime,
      };
      updateProjectRequest(request);
    };

    showConfirmationDialog({
      title: "",
      message: "",
      type: "confirm",
      onOkButtonClicked: updateProject,
    });
  };

  const onListenUpdateProjectResponse = (): void => {
    if (!updateProjectResponse) return;

    showToast({
      design: "completion",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
    });
    closeModal(true);
  };
  useEffect(onListenUpdateProjectResponse, [updateProjectResponse]);

  const getContent = () => {
    return (
      <>
        <div
          style={{
            paddingTop: "10px",
            paddingBottom: "30px",
            marginLeft: "30px",
          }}
        >
          <FrVrfFormPanel columns={1}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <FrVrfRadioButtonField
                labelContent="Necessity of Securing new Budget"
                style={{
                  "--vrf-radio-button-field-label-width": "350px",
                }}
                optionItems={pageState.codes.pjtBdgtAcqstnReqType}
                checked={pageState.edited.pjtBdgtAcqstnReqTypeCd}
                required
                constraint="IsNotBlank(value)"
                onRadioCheckedChanged={onChangeValueHandle(
                  "pjtBdgtAcqstnReqTypeCd",
                )}
                errorMessage={errorMessages(
                  pageState.fieldPath.pjtBdgtAcqstnReqTypeCd,
                )}
              />
              <FrVrfButton
                icon="frIcon:question"
                theme="tertiary"
                size="small"
                tooltipVisible={true}
                tooltipPosition="below"
                tooltipContent={MANUAL_LINK.tooltip}
                style={{
                  "--vrf-tooltip-max-width": "120px",
                  marginRight: "50px",
                }}
                onClicked={() => {
                  window.open(MANUAL_LINK.value);
                }}
              />
            </div>
            <div>
              <FrVrfRadioButtonField
                labelContent="Consensus of budget usage between project team and IE team"
                style={{
                  "--vrf-radio-button-field-label-width": "350px",
                }}
                optionItems={pageState.codes.statsOfAgreementOnBudgetaryType}
                checked={pageState.edited.statsOfAgreementOnBudgetaryTypeCd}
                required
                constraint="IsNotBlank(value)"
                onRadioCheckedChanged={onChangeValueHandle(
                  "statsOfAgreementOnBudgetaryTypeCd",
                )}
                errorMessage={errorMessages(
                  pageState.fieldPath.statsOfAgreementOnBudgetaryTypeCd,
                )}
              />
            </div>
          </FrVrfFormPanel>
          <FrVrfFooter>
            <FrVrfButton
              content="Cancel"
              theme="secondary"
              size="large"
              onClicked={onClickCancel}
            />
            <FrVrfButton
              content="Save"
              theme="primary"
              size="large"
              disabled={
                JSON.stringify(pageState.saved) ===
                  JSON.stringify(pageState.edited) ||
                pageState.status.invalidList.length !== 0
              }
              onClicked={onClickSave}
            />
          </FrVrfFooter>
        </div>
      </>
    );
  };

  return createModal("Cost Estimation", getContent(), onClickCancel, "large");
};
